import React from 'react';
import moment from 'moment';

import { MCButton, MCText, SearchTextField } from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';
import { TableLibrary } from 'src/components/complex';

import {
  addressObject,
  lang,
  permissions,
  remCss,
  scssVariables as variables,
  withRouter
} from 'src/helpers';

import TreatmentDetail from 'src/components/treatments/TreatmentDetail';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import ShortInfo from 'src/components/user/ShortInfo';
import HospitalSelect from 'src/components/complex/HospitalSelect';
import LANG_CONST from 'src/helpers/lang/const';

import model from './model';

class Treatments extends BaseComponent {
  componentDidMount() {
    model.subscribe(this.subscription);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  checkPermission = () => {
    const auth = model.state.user || {};
    if (auth.isDoctor) return true;
    const isOwner = auth.managerToClinic === auth.id;
    const isAdmin = !!auth.managerAdmin;
    const hospitalIds = auth.managerAccess?.filter((a) => !!a.treatments).map((a) => a.hospitalId);

    return isOwner || isAdmin || (hospitalIds && hospitalIds.length > 0);
  };

  renderDetailActions = (item) => {
    let { openId } = model.state;
    const medium = !this.props.screenDetail?.isLargeScreen;

    return (
      <div className={'mc-flex'}>
        <div className={'mc-grow mc-flex-col'}>
          {medium && (
            <>
              <div className={'mc-flex-0'}>
                <MCText label={LANG_CONST.diagnostic} />
                <MCText label={': ' + item.data?.diagnosis} flex />
              </div>
              <div className={'mc-flex-0'}>
                <MCText label={LANG_CONST.date} />
                <MCText label={': ' + moment(item.date).format('DD - MMMM - YYYY')} flex />
              </div>
            </>
          )}
        </div>
        <MCButton
          // onClick={() => model.setState({ openId: openId === item.id ? null : item.id })}
          tiny
          quaternary
          light
          radius
          hover={true}
        >
          <span
            className={'mc-rotate ' + (openId === item.id ? 'mc-rotate-180' : '')}
            style={{ height: remCss(44) }}
          >
            <SVGIcon
              iconname={BASE_ICONS.DOWN}
              width={44}
              padding={5}
              fill={variables['colors-mc-orange']}
            />
          </span>
        </MCButton>
      </div>
    );
  };

  render() {
    let loading = model.refreshing();
    let noResults = model.noResults();
    let searchQuery = model.listInput('search');
    let items = model.items();
    let { total = 0 } = model.getCurrent();
    let { openId, user } = model.state;

    if (!this.checkPermission())
      return (
        <div className={'mc-container-content'}>
          <div className={'mc-container-content__header mc-flex-15'}>
            <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.treatments)}</span>
          </div>
          <div className={'mc-container-content__body'}>
            <MCText
              weight={500}
              size={20}
              label={LANG_CONST.manager_no_permissions.treatments}
              primary
              style={{ textAlign: 'center', margin: 'auto' }}
            />
          </div>
        </div>
      );
    const medium = !this.props.screenDetail?.isLargeScreen;

    const columns = [
      {
        label: LANG_CONST.patients,
        resize: true,
        sort: { sortKey: 'patient' },
        renderCell: (item) => {
          const patient = item.patient || item.toUser || {};
          return <ShortInfo user={patient} to={permissions.profilePath(patient)} />;
        }
      },
      !medium && {
        label: LANG_CONST.diagnostic,
        resize: true,
        sort: { sortKey: 'text' },
        renderCell: (item) => item.data?.diagnosis
      },
      {
        label: (
          <HospitalSelect
            template={6}
            placeholder={LANG_CONST.location}
            expandOptions={[{ id: 'virtual', hospital: lang.t(LANG_CONST.virtual) }]}
            apiConfig={{
              url: model.state?.user?.isManager ? 'clinic/manager/hospitals' : 'hospital/list',
              activeTreatments: true,
              clinicIds: model.state.user?.clinicIds,
              hospitalIds: model.state.user?.hospitalIds
            }}
            name={'hospitalId'}
            value={model.input('hospitalId')}
            onChange={(n, v) => {
              if (v?.toString() === model.input('hospitalId')?.toString()) return;
              model.setStateSilent({
                pageIndex: 0,
                [n]: v
              });
              model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
              model.refresh();
            }}
          />
        ),
        resize: true,
        renderCell: (item) => {
          if (!item.hospitalId) return lang.t(LANG_CONST.virtual);
          if (!item.hospital) return lang.t(LANG_CONST.virtual);

          return (
            <div className={'mc-flex-0-col'} style={{ minWidth: 0 }}>
              <div className={'mc-text-418 mc-text-truncate'}>
                {item.hospital.hospital?.split('_')[0]}
              </div>
              <div className={'mc-label-416 mc-text-truncate'}>
                {
                  addressObject({
                    cityAddress: item.hospital.city,
                    stateAddress: item.hospital.state,
                    countryAddress: item.hospital.country
                  }).fullAddressNameReverse
                }
              </div>
            </div>
          );
        }
      },
      !medium && {
        label: LANG_CONST.date,
        resize: true,
        sort: { sortKey: 'date' },
        renderCell: (item) => moment(item.date).format('DD - MMMM - YYYY')
      },
      !user.isDoctor
        ? {
            label: LANG_CONST.doctors,
            resize: true,
            sort: { sortKey: 'doctor' },
            renderCell: (item) => {
              const doctor = item.doctor || item.fromUser || {};
              return <ShortInfo user={doctor} to={permissions.profilePath(doctor)} />;
            }
          }
        : {
            label: LANG_CONST.status,
            resize: true,
            sort: { sortKey: 'status' },
            renderCell: (item) =>
              item.data.feedback?.status && (
                <button className={'mc-status-' + item.data.feedback?.status}>
                  {lang.t(LANG_CONST.constants.health_statuses[item.data.feedback?.status])}
                </button>
              )
          },
      !medium && {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'end' },
        width: `${remCss(100)}px`,
        renderCell: this.renderDetailActions
      }
    ].filter((c) => !!c);

    return (
      <div className={'mc-container-content'}>
        <div className={'mc-container-content__header mc-flex-15'}>
          <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.treatments)}</span>
          <SearchTextField
            width={300}
            template={'2'}
            onChange={(text) => {
              model.setStateSilent({
                pageIndex: 0
              });
              model.setListStateSilent({ items: [], total: 0 }, 'search');
              model.handleChangeSearch(text);
              if (!text) {
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.refresh();
              }
            }}
            value={model.listInput('search')}
          />
        </div>
        <div className={'mc-container-content__body'}>
          <div className={'mc-container-content__body-content'} style={{ marginTop: remCss(25) }}>
            <TableLibrary
              key={medium ? 1 : 0}
              renderExtend={(item) => {
                return (
                  <>
                    {medium && (
                      <div
                        className={'mc-pointer'}
                        onClick={() => {
                          model.setState({ openId: openId === item.id ? null : item.id });
                        }}
                      >
                        {this.renderDetailActions(item)}
                      </div>
                    )}
                    {openId === item.id && <TreatmentDetail item={item} auth={user} />}
                  </>
                );
              }}
              onRowClick={(item) => {
                model.setState({ openId: openId === item.id ? null : item.id });
              }}
              className={medium ? 'mc-row2' : ''}
              columns={columns}
              data={items}
              loading={loading}
              noResults={noResults}
              searchQuery={searchQuery}
              sortBy={model.state.sortBy}
              selectedPage={model.state.pageIndex}
              total={total}
              limit={model.listLimit}
              onSortChange={(sortBy) => {
                model.handleChange('sortBy', sortBy);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
              onSetPage={(v) => {
                model.handleChange('pageIndex', v);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Treatments);
